import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Lightbulb, Bug, HelpCircle, MoreHorizontal, CheckCircle2,
  Inbox, MessageSquare, AlertTriangle, RotateCw, PackageCheck,
} from 'lucide-react';
import { Button, Spinner, Badge } from '@/components/ui';
import {
  submitFeedback, fetchMyFeedback,
  type FeedbackCategory, type FeedbackPost,
} from '@/lib/api';
import {
  STATUS_ETIKETT, KATEGORI_ETIKETT,
  harSvar, vantarPaOss, avvisadUtanMotivering, sortera, datumtext, statusrad,
} from '@/lib/feedback-status';
import { cn } from '@/lib/utils';
import { CHANGELOG } from '@/lib/version';

/**
 * Releasen som löste ärendet, om den går att hitta i ändringsloggen.
 *
 * Versionen kommer från CRM och ändringsloggen bor i koden — de kan hamna i
 * otakt, till exempel om ett ärende märks med en version som ännu inte
 * släppts. Då visas numret utan titel i stället för att raden försvinner:
 * "åtgärdat i 2.3.0" är fortfarande sant och användbart, medan en påhittad
 * titel eller en tom rad inte är det.
 */
function releaseFor(version: string | null) {
  if (!version) return null;
  return CHANGELOG.find((p) => p.version === version) ?? null;
}

const CATEGORIES: { value: FeedbackCategory; label: string; description: string; icon: typeof Lightbulb }[] = [
  { value: 'forbattring', label: 'Förbättringsförslag', description: 'Idé på en ny funktion eller förändring', icon: Lightbulb },
  { value: 'bugg', label: 'Bugg / problem', description: 'Något beter sig inte som det ska', icon: Bug },
  { value: 'fraga', label: 'Fråga', description: 'Något du vill veta om hur ELvis fungerar', icon: HelpCircle },
  { value: 'annat', label: 'Annat', description: 'Något som inte passar i kategorierna ovan', icon: MoreHorizontal },
];

const MIN_LENGTH = 5;
const MAX_LENGTH = 4000;

/**
 * Feedback som egen sida i stället för en modal uppe till höger.
 *
 * Modalen tog emot men visade aldrig något tillbaka. Tretton poster hade
 * skickats in sedan maj — sex av dem buggar — utan att avsändaren kunde se
 * att de fanns kvar, och utan att någon fått svar.
 *
 * ⚠️ Det är HISTORIKEN som gör en flik meningsfull. Ett formulär på en egen
 * sida är fortfarande samma brevlåda utan botten, bara större. Listan under
 * formuläret är alltså inte en bonus — den är skälet till att sidan finns.
 */
export default function FeedbackPage() {
  const [category, setCategory] = useState<FeedbackCategory>('forbattring');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [poster, setPoster] = useState<FeedbackPost[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);

  // ⚠️ EN dagsstämpel per rendering, inte ett `new Date()` per rad. Två anrop
  // kan hamna på var sin sida om midnatt, och då står det olika datum på två
  // rader för samma tidpunkt.
  const idag = new Date();

  // ⚠️ Löpnummer mot kapplöpning.
  //
  // `load` körs både vid montering och direkt efter ett inskick. Två anrop
  // kan alltså vara i luften samtidigt, och svaren kommer inte nödvändigtvis
  // i den ordning de skickades — det långsammare svaret skriver då över det
  // nyare. Utfallet är den nyss inskickade posten som blinkar till och
  // försvinner, vilket är precis det sidan finns för att motbevisa.
  //
  // Bara det senaste anropet får röra state; äldre svar kastas.
  const senasteHamtning = useRef(0);

  const load = useCallback(async () => {
    const min = ++senasteHamtning.current;
    // Felet nollas när hämtningen LYCKATS, inte när den startar.
    //
    // Ett `setListError(null)` här uppe körs synkront i effekten som anropar
    // load, vilket ger en extra renderrunda innan datan ens är på väg. Och
    // funktionellt är det sämre: felrutan blinkar bort en kort stund vid
    // varje omhämtning, även när omhämtningen misslyckas igen.
    try {
      const data = await fetchMyFeedback();
      if (min !== senasteHamtning.current) return;
      setPoster(data);
      setListError(null);
    } catch (e) {
      if (min !== senasteHamtning.current) return;
      // Ingen tyst tom lista vid fel. Den som skickat in tretton saker och
      // möter "Du har inte skickat något än" drar slutsatsen att systemet
      // tappat dem.
      setListError(e instanceof Error ? e.message : 'Kunde inte hämta dina ärenden.');
    } finally {
      if (min === senasteHamtning.current) setLoadingList(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Kvittensrutan släcks av en effekt, inte av ett setTimeout i submit-
  // hanteraren. En lös timer överlever att komponenten lämnas och försöker
  // då sätta state på något som inte finns kvar; skickar man två gånger
  // snabbt släcker dessutom den första timern den andras kvittens.
  useEffect(() => {
    if (!justSubmitted) return;
    const id = setTimeout(() => setJustSubmitted(false), 6000);
    return () => clearTimeout(id);
  }, [justSubmitted]);

  const handleSubmit = async () => {
    setFormError(null);
    const trimmed = message.trim();
    if (trimmed.length < MIN_LENGTH) {
      setFormError(`Skriv minst ${MIN_LENGTH} tecken så vi förstår vad du menar.`);
      return;
    }
    if (trimmed.length > MAX_LENGTH) {
      setFormError(`Meddelandet är för långt (max ${MAX_LENGTH} tecken).`);
      return;
    }

    setSubmitting(true);
    try {
      await submitFeedback(category, trimmed);
      setMessage('');
      setCategory('forbattring');
      setJustSubmitted(true);
      // Listan hämtas om så att posten syns direkt. Att bara visa ett
      // "tack" och lämna listan oförändrad hade sett ut som att den inte
      // sparades — vilket är precis den misstanken sidan finns för att ta bort.
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Något gick fel — försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  const sorterade = poster ? sortera(poster) : [];
  const obesvarade = sorterade.filter(vantarPaOss).length;

  return (
    <div className="flex-1 overflow-y-auto p-5 lg:p-6">
      <div className="max-w-[860px] mx-auto pb-10 space-y-5">

        {/* ── Skicka in ── */}
        <section className="rounded-2xl border border-ink-100 bg-surface p-5 lg:p-6">
          <h2 className="text-[15px] font-bold text-ink-900 mb-1">Säg till om något</h2>
          <p className="text-[13px] text-ink-500 mb-5">
            Det du skriver hamnar hos Samify och blir ett ärende du kan följa här nedanför.
          </p>

          {/* ⚠️ NATIVE radioknappar, inte knappar med role="radio".
              Kategorierna är ett VAL av ett alternativ, inte fyra oberoende
              av/på-växlar — men att bara sätta role="radio" deklarerar en
              semantik utan att uppfylla den: en radiogrupp ska gå att stega
              med piltangenter, ha EN tabbstopp och säga "3 av 4" i en
              skärmläsare. Det kräver roving tabIndex och egen
              tangentbordshantering.
              Med `input[type=radio]` gör webbläsaren hela den modellen
              gratis, och rätt beteende blir svårare att råka förstöra än att
              få rätt. Inputen döljs visuellt; fokusringen flyttas till kortet
              med `peer-focus-visible`. */}
          <fieldset className="mb-6 border-0 p-0 m-0">
            <legend className="block text-[11px] font-bold uppercase tracking-[0.14em] text-ink-500 mb-3 p-0">
              Vad gäller det?
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CATEGORIES.map((opt) => {
                const Icon = opt.icon;
                const active = category === opt.value;
                return (
                  <label key={opt.value} className="relative cursor-pointer">
                    <input
                      type="radio"
                      name="feedback-kategori"
                      value={opt.value}
                      checked={active}
                      onChange={() => setCategory(opt.value)}
                      disabled={submitting}
                      className="sr-only peer"
                    />
                    <span
                      className={cn(
                        'group flex items-start gap-3.5 text-left p-4 rounded-lg border transition-colors',
                        'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2',
                        active
                          ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                          : 'border-ink-200 bg-surface hover:border-ink-300 hover:bg-ink-50',
                      )}
                    >
                      <span className={cn(
                        'w-9 h-9 rounded-md flex items-center justify-center shrink-0 border transition-colors',
                        active
                          ? 'bg-brand-solid border-brand-500 text-brand-on-solid'
                          : 'bg-ink-50 border-ink-200 text-ink-500 group-hover:bg-ink-100',
                      )}>
                        <Icon size={18} strokeWidth={2} aria-hidden />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className={cn('block text-[14px] font-bold leading-tight',
                          active ? 'text-brand-900' : 'text-ink-900')}>
                          {opt.label}
                        </span>
                        <span className="block text-[12px] text-ink-500 mt-1 leading-snug">
                          {opt.description}
                        </span>
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <label htmlFor="feedback-message"
            className="block text-[11px] font-bold uppercase tracking-[0.14em] text-ink-500 mb-3">
            Berätta lite mer
          </label>
          <textarea
            id="feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={MAX_LENGTH}
            rows={6}
            placeholder="Vad gjorde du, vad hände, vad väntade du dig?"
            className="w-full px-5 py-4 rounded-2xl border border-ink-100 bg-surface text-[14px] text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 focus:border-brand-500 resize-none transition-all leading-relaxed"
            disabled={submitting}
          />
          <div className="flex justify-between mt-2 text-[11px] text-ink-500 tabular-nums px-1">
            <span>{message.trim().length < MIN_LENGTH ? `Minst ${MIN_LENGTH} tecken` : ''}</span>
            <span>{message.length} / {MAX_LENGTH.toLocaleString('sv-SE')}</span>
          </div>

          {formError && (
            /* role="alert" — annars är felet bara röd text som den som inte
               ser skärmen aldrig får veta om. Knappen förblir intryckbar och
               ingenting händer, vilket läses som att systemet hängt sig. */
            <div
              role="alert"
              className="mt-4 text-[12px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
            >
              {formError}
            </div>
          )}

          {justSubmitted && (
            /* role="status" av samma skäl som role="alert" på felet ovan.
               Kvittensen är hela poängen med inskicket — den som inte ser
               skärmen ska inte behöva gissa om det gick fram. `status` och
               inte `alert`: beskedet är lugnt och ska inte avbryta det
               användaren håller på med. */
            <div
              role="status"
              className="mt-4 flex items-center gap-2.5 text-[13px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3"
            >
              <CheckCircle2 size={16} strokeWidth={2} className="text-emerald-600 shrink-0" />
              Tack! Den ligger nu i listan nedan och vi får ett mejl direkt.
            </div>
          )}

          <div className="flex justify-end mt-5">
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || message.trim().length < MIN_LENGTH}
            >
              {submitting ? 'Skickar…' : 'Skicka till Samify'}
            </Button>
          </div>
        </section>

        {/* ── Dina ärenden ── */}
        <section>
          <div className="flex items-center justify-between mb-3 px-1">
            <h2 className="text-[15px] font-bold text-ink-900">Dina ärenden</h2>
            {poster && poster.length > 0 && (
              <span className="text-[12px] text-ink-500">
                {obesvarade > 0
                  ? `${obesvarade} väntar på svar från oss`
                  : 'Alla har fått svar'}
              </span>
            )}
          </div>

          {loadingList ? (
            <div className="flex justify-center py-10">
              <Spinner size={24} className="text-brand-500" />
            </div>
          ) : listError ? (
            <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-[13px] text-red-800">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={16} strokeWidth={2} className="shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold">Kunde inte hämta dina ärenden.</p>
                  <p className="mt-1 text-red-700">{listError}</p>
                  <button
                    type="button"
                    onClick={() => { setLoadingList(true); void load(); }}
                    className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold text-red-900 hover:underline"
                  >
                    <RotateCw size={13} strokeWidth={2} /> Försök igen
                  </button>
                </div>
              </div>
            </div>
          ) : sorterade.length === 0 ? (
            <div className="rounded-2xl border border-ink-100 bg-surface p-8 text-center">
              <Inbox size={22} strokeWidth={2} className="text-ink-500 mx-auto mb-2" />
              <p className="text-[13.5px] font-bold text-ink-900">Du har inte skickat något än</p>
              <p className="text-[12.5px] text-ink-500 mt-1">
                Det du skickar in dyker upp här, med svar när vi hunnit titta.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {sorterade.map((p) => (
                <article key={p.id} className="rounded-2xl border border-ink-100 bg-surface p-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="text-[12px] text-ink-500">
                        {KATEGORI_ETIKETT[p.category] ?? p.category}
                        {' · '}
                        {datumtext(p.createdAt, idag)}
                        {/* Ärendenumret finns här för ETT syfte: att kunna
                            hänvisa till rätt post i ett mejl eller samtal.
                            Saknas det är posten ännu inte speglad till
                            Samifys kö, och då ska raden inte påstå något. */}
                        {p.arendenummer && (
                          <span className="ml-1.5 font-mono text-[11px] text-ink-500">
                            {p.arendenummer}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge variant={p.status === 'klar' ? 'success' : vantarPaOss(p) ? 'warning' : 'neutral'}>
                      {STATUS_ETIKETT[p.status]}
                    </Badge>
                  </div>

                  <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-ink-900 bg-ink-50 rounded-xl px-4 py-3">
                    {p.message}
                  </p>

                  <p className="mt-2 text-[11.5px] text-ink-500">{statusrad(p, idag)}</p>

                  {/* Vilken release som löste ärendet. "Klar" säger att något
                      gjorts; den här säger var det finns. Utan den vet den som
                      rapporterat ett fel fortfarande inte om rättelsen sitter i
                      appen de har uppe.

                      ⚠️ Visas BARA när ärendet är klart. Öppnas ett löst ärende
                      igen står versionen kvar i databasen — med flit, den är
                      sann och behövs när ärendet stängs på nytt — men raden
                      hade då sagt "Åtgärdat i 2.2.0" ovanför en status som
                      säger att arbetet pågår. Samma sorts självmotsägelse som
                      läst-stämpeln en gång skrev ut i klartext. Villkoret sitter
                      i vyn och inte i mottagaren: att nollställa fältet vid
                      återöppning hade kastat bort uppgiften i stället för att
                      låta bli att visa den. */}
                  {p.atgardadIVersion && p.status === 'klar' && (
                    <div className="mt-2 flex items-start gap-1.5 text-[11.5px] text-emerald-800">
                      <PackageCheck size={13} strokeWidth={2} className="mt-px shrink-0" />
                      <span>
                        Åtgärdat i version{' '}
                        <span className="font-semibold tabular-nums">{p.atgardadIVersion}</span>
                        {releaseFor(p.atgardadIVersion) && (
                          <>
                            {' — '}
                            <span className="text-emerald-900">{releaseFor(p.atgardadIVersion)!.title}</span>
                          </>
                        )}
                        . Öppna Uppdateringar i menyn för vad som ändrades.
                      </span>
                    </div>
                  )}

                  {harSvar(p) && (
                    <div className="mt-3 rounded-xl border border-brand-100 bg-brand-50 px-4 py-3">
                      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-900">
                        <MessageSquare size={12} strokeWidth={2} />
                        Svar från Samify
                        {p.besvaradAt && (
                          <span className="font-normal normal-case tracking-normal text-brand-700">
                            · {datumtext(p.besvaradAt, idag)}
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 whitespace-pre-line text-[13px] leading-relaxed text-ink-900">
                        {p.svar}
                      </p>
                    </div>
                  )}

                  {/* Ett nej utan motivering är det sämsta utfallet: du ser
                      att vi sagt nej men inte varför. Sådana markeras så att
                      de går att hitta och rätta. */}
                  {avvisadUtanMotivering(p) && (
                    <p className="mt-2 text-[11.5px] text-amber-700">
                      Avvisad utan motivering — säg till så förklarar vi.
                    </p>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
